import {
    AbstractControl, FormControl, FormGroup, FormRecord, ValidationErrors, ValidatorFn, Validators
} from "@angular/forms";
import { DateTime } from "luxon";
import {
    Observable, filter, map, merge, tap
} from "rxjs";
import {
    EnumUtil, ILocalizableModel, IValidatedModel, Language, LocalizationUtil, ModelAction, ModelUtil, ObjectUtil, StringUtil
} from "@shopliftr/common-js/data";
import {
    RequestUtil, UpdateRequest
} from "@shopliftr/common-js/shared";

import { EnhancedFormDirective } from "../enhanced-form.directive";
import { IBulkUpdateField } from "../interfaces/bulk-update-field";

/** Key of the nested group holding the base fields' clear intents. */
const CLEAR_INTENTS_KEY = "clearIntents";

/** Suffix of a localized field's clear-intent control, a sibling of the value in its language group. */
const LOCALIZED_CLEAR_INTENT_SUFFIX = "ClearIntent";

/** Every non-default language, in `Language` declaration order. */
const LOCALIZED_LANGUAGES: ReadonlyArray<Language> = EnumUtil.getValues(Language, [Language.English]);

/** A value control and the clear-intent control that belongs to it. */
interface IControlPair {

    /** The field's value control. */
    value: AbstractControl;

    /** The field's clear-intent control. */
    intent: AbstractControl;
}

/** Anything that may carry an `id`, such as an embedded model. */
interface IIdentified {

    /** The embedded model's identifier, when it has one. */
    readonly id?: unknown;
}

/** Anything that may be a model with a `clone()` method. */
interface ICloneable {

    /** Copies the model. */
    clone?(): unknown;
}

/** One resolved non-leave change for one value path. */
interface IBulkUpdateChange {

    /** The field's model path. */
    readonly path: string;

    /** The language the change applies to; English for the base value. */
    readonly language: Language;

    /** Whether the value is written or removed. */
    readonly state: "set" | "clear";

    /** The sanitized form value; empty for a clear. */
    readonly value: unknown;
}

/** Anything that may be a model with a `serialize()` method. */
interface ISerializable {

    /** The model's plain-object form. */
    serialize?(): unknown;
}

/** What `BulkUpdateFormUtil.seed` returns. */
export interface IBulkUpdateSeed {

    /** Form path → seed value: the common value when every record agrees, `null` when they disagree or all are empty. */
    readonly values: Readonly<Record<string, unknown>>;

    /** Form paths whose records disagree — shown blank with a "Mixed values" hint. */
    readonly mixed: ReadonlySet<string>;
}

/** A model a Bulk Update Form can write: validated, persisted by `id`, versioned by `esVersion`. */
export type BulkUpdatableModel<M> = IValidatedModel<M> & {
    readonly id: string;
    readonly esVersion?: number;
};

/**
 * The slice of `EnhancedFormDirective` the request builder reads. The directive satisfies it
 * directly; specs can supply a plain object.
 */
export type BulkUpdateFormState = Pick<EnhancedFormDirective, "formGroup" | "isChangedAt" | "snapshotValueAt">;

/**
 * Stateless helpers for the Bulk Update Form pattern (precedent: `LocalizationFormUtil`).
 *
 * Every field is independently *leave*, *set*, or *clear*, derived from the form value against
 * the `populate()` snapshot plus an explicit boolean clear intent. Paths:
 *
 * | Field | Value control | Clear intent |
 * |---|---|---|
 * | base, not required | `P` | `clearIntents.P` |
 * | base, required | `P` | none — never cleared |
 * | localized, language `L` | `localizations.L.F` | `localizations.L.FClearIntent` |
 *
 * Per value path (from the form value, `isChangedAt`, the `populate()` snapshot and the intent):
 *
 * | Value (sanitized) | Condition | State |
 * |---|---|---|
 * | non-empty | `isChangedAt(path)` | **set** |
 * | non-empty | unchanged | leave |
 * | empty | not clearable (base value of a required field) | leave |
 * | empty | intent `true` | **clear** |
 * | empty | snapshot non-empty (a common seed was emptied) | **clear** |
 * | empty | snapshot empty (mixed or all-empty, incl. type-then-delete) | leave |
 *
 * Localized intents are flat booleans beside their value, not a nested group:
 * `LocalizationSelectorComponent` treats any truthy control value in a language group as content,
 * and a nested group's value is always a (truthy) object.
 *
 * **Checkbox fields** (`kind: "checkbox"`) are tri-state: the control holds `true`, `false`, or
 * `null`. An unset model value counts as `false`, so records agree when their effective booleans
 * agree; a mixed selection is seeded `null` (indeterminate, "Mixed values"). There is no clear
 * intent — `false` is a boolean's cleared state.
 *
 * | Value | Condition | State |
 * |---|---|---|
 * | `true` / `false` | differs from the seed | **set** (written to each record whose effective value differs) |
 * | `true` / `false` | equals the seed | leave |
 * | `null` | — | leave |
 *
 * **Date-range fields** (`kind: "dateRange"`, `path` start, `linkedPath` end) are seeded, set and
 * cleared as one unit: the pair is common only when every record has the same start *and* end,
 * otherwise both paths are seeded `null` and listed in `mixed`. Form values may be `Date` or Luxon
 * `DateTime`; requests carry `Date`. `clearIntents.<path>` covers the pair and `required` protects
 * both ends.
 *
 * | Start / end (both sanitized) | Condition | State |
 * |---|---|---|
 * | both non-empty | either differs from the seed (by instant) | **set** both |
 * | both non-empty | both equal the seed | leave |
 * | one empty | — | leave (`linkedRangeValidator` blocks submit) |
 * | both empty | required | leave |
 * | both empty | intent `true`, or seed non-empty | **clear** both |
 * | both empty | seed empty | leave |
 */
export class BulkUpdateFormUtil {

    /**
     * The form path of a field's value control.
     *
     * @param path - The field's model path
     * @param language - Omitted or English for the base control
     */
    static valuePath(path: string, language: Language = Language.English): string {

        return language === Language.English ? path : `localizations.${language}.${path}`;
    }

    /**
     * The form path of a field's clear-intent control.
     *
     * @param path - The field's model path
     * @param language - Omitted or English for the base intent
     */
    static clearIntentPath(path: string, language: Language = Language.English): string {

        return language === Language.English ? `${CLEAR_INTENTS_KEY}.${path}` : `localizations.${language}.${path}${LOCALIZED_CLEAR_INTENT_SUFFIX}`;
    }

    /**
     * Seed values for every bulk path: the common value when every record agrees, `null` (and
     * the path in `mixed`) when they disagree. Embedded models compare by `id`. Localized fields
     * are seeded per language. Pass the result to `EnhancedFormDirective.populate()` as a `Map`.
     *
     * @param records - The selection
     * @param fields - Every field the form can change across the selection
     */
    static seed<M>(records: ReadonlyArray<BulkUpdatableModel<M>>, fields: ReadonlyArray<IBulkUpdateField<M>>): IBulkUpdateSeed {

        const values: Record<string, unknown> = {};
        const mixed = new Set<string>();

        if (!records.length) {
            return {
                values,
                mixed
            };
        }

        const localizedPaths = BulkUpdateFormUtil._localizedFieldPaths(records[0], fields);

        for (const field of fields) {
            if (field.kind === "checkbox") {
                BulkUpdateFormUtil._seedPath(
                    field.path,
                    records.map((record) => Boolean((record as unknown as Record<string, unknown>)[field.path])),
                    values,
                    mixed
                );
                continue;
            }
            if (field.kind === "dateRange") {
                BulkUpdateFormUtil._seedRange(records, field, values, mixed);
                continue;
            }

            BulkUpdateFormUtil._seedPath(
                field.path,
                records.map((record) => (record as unknown as Record<string, unknown>)[field.path]),
                values,
                mixed
            );

            if (localizedPaths.includes(field.path)) {
                for (const language of LOCALIZED_LANGUAGES) {
                    BulkUpdateFormUtil._seedPath(
                        BulkUpdateFormUtil.valuePath(field.path, language),
                        records.map((record) => BulkUpdateFormUtil._localizedValue(record, field.path, language)),
                        values,
                        mixed
                    );
                }
            }
        }

        return {
            values,
            mixed
        };
    }

    /**
     * The nested `clearIntents` group: one `false` boolean per non-required base field. Checkbox
     * fields have no clear intent; a date-range field's single intent is keyed by its start path.
     *
     * @typeParam C - The group's control shape, e.g. `{ marketArea: FormControl<boolean> }`
     * @param fields - Every field the form can change across the selection
     */
    static createClearIntents<M, C extends { [K in keyof C]: FormControl<boolean> } = Record<string, FormControl<boolean>>>(
        fields: ReadonlyArray<IBulkUpdateField<M>>
    ): FormGroup<C> {

        const controls: Record<string, FormControl<boolean>> = {};

        for (const field of fields) {
            if (BulkUpdateFormUtil._hasClearIntent(field)) {
                controls[field.path] = new FormControl(false, { nonNullable: true });
            }
        }

        // Keys are only known at runtime from `fields`; this single cast is the contained cost,
        // as in LocalizationFormUtil.createLocalizationsRecord.
        return new FormGroup(controls) as unknown as FormGroup<C>;
    }

    /**
     * Adds a `false` `<field>ClearIntent` control to every language group for each localized bulk
     * field (required or not — only the base value of a required field is protected). Call on the
     * record from `LocalizationFormUtil.createLocalizationsRecord` before the form is populated.
     *
     * @param localizations - The form's `localizations` record
     * @param model - Any instance of the model, for its `@localized()` metadata
     * @param fields - Every field the form can change across the selection
     * @returns The same record, for use in a field initializer
     */
    static addLocalizedClearIntents<M, R extends FormRecord<FormGroup>>(
        localizations: R,
        model: BulkUpdatableModel<M>,
        fields: ReadonlyArray<IBulkUpdateField<M>>
    ): R {

        const localizedPaths = BulkUpdateFormUtil._localizedFieldPaths(model, fields);

        for (const language of LOCALIZED_LANGUAGES) {
            const group = localizations.controls[language];
            for (const path of localizedPaths) {
                group.addControl(`${path}${LOCALIZED_CLEAR_INTENT_SUFFIX}`, new FormControl(false, { nonNullable: true }));
            }
        }

        return localizations;
    }

    /**
     * Resets a clear intent to `false` whenever its field receives a non-empty value (the user
     * typed or picked something new). Idempotent: `populate()` and the localization selector's
     * `enable()`/`disable()` re-emit `valueChanges` and leave a pending intent untouched. Subscribe
     * once in `ngOnInit` with `takeUntilDestroyed`.
     *
     * @param formGroup - The group holding the value and clear-intent controls
     * @param model - Any instance of the model, for its `@localized()` metadata
     * @param fields - Every field the form can change across the selection
     */
    static clearIntentResets$<M>(
        formGroup: FormGroup,
        model: BulkUpdatableModel<M>,
        fields: ReadonlyArray<IBulkUpdateField<M>>
    ): Observable<void> {

        const pairs: Array<IControlPair> = [];

        for (const field of fields) {
            if (BulkUpdateFormUtil._hasClearIntent(field)) {
                pairs.push({
                    value: formGroup.get(BulkUpdateFormUtil.valuePath(field.path)),
                    intent: formGroup.get(BulkUpdateFormUtil.clearIntentPath(field.path))
                });
                if (field.kind === "dateRange") {
                    pairs.push({
                        value: formGroup.get(BulkUpdateFormUtil.valuePath(field.linkedPath)),
                        intent: formGroup.get(BulkUpdateFormUtil.clearIntentPath(field.path))
                    });
                }
            }
        }

        for (const path of BulkUpdateFormUtil._localizedFieldPaths(model, fields)) {
            for (const language of LOCALIZED_LANGUAGES) {
                pairs.push({
                    value: formGroup.get(BulkUpdateFormUtil.valuePath(path, language)),
                    intent: formGroup.get(BulkUpdateFormUtil.clearIntentPath(path, language))
                });
            }
        }

        return merge(...pairs
            .filter((pair) => pair.value && pair.intent)
            .map(({
                value, intent
            }) => value.valueChanges.pipe(
                filter((current: unknown) => intent.value === true && !BulkUpdateFormUtil._isEmpty(current)),
                tap(() => intent.setValue(false)),
                map((): void => undefined)
            )));
    }

    /**
     * One `ModelAction.ClientUpdate` request per record that actually changes. `fields` always
     * comes from `original.getChanges(merged)` (never a hand-built partial, which would overwrite
     * localization arrays); clears go to `removeFields` (never `null`); a required path is never
     * in `removeFields`; every request carries the record's `esVersion`. Returns `[]` when
     * nothing needs writing, so the dialog can close without calling the API.
     *
     * @typeParam M - The model the form updates
     * @typeParam O - The request options type, when the API needs them (e.g. `DataEntryDealRequestOptions`)
     * @param originals - Clones of the selection as loaded
     * @param form - The form's `EnhancedFormDirective` (or any `BulkUpdateFormState`)
     * @param fields - Every field the form can change across the selection
     * @param options - Request options carried by every request; omitted when the API has none
     */
    static createUpdateRequests<M, O = void>(
        originals: ReadonlyArray<BulkUpdatableModel<M>>,
        form: BulkUpdateFormState,
        fields: ReadonlyArray<IBulkUpdateField<M>>,
        options?: O
    ): Array<UpdateRequest<M, O>> {

        if (!originals.length) {
            return [];
        }

        const changes = BulkUpdateFormUtil._resolveChanges(originals[0], form, fields);
        const requiredPaths = new Set<string>();
        for (const field of fields.filter((candidate) => candidate.required)) {
            requiredPaths.add(field.path);
            if (field.kind === "dateRange") {
                requiredPaths.add(field.linkedPath);
            }
        }

        return originals
            .map((original) => BulkUpdateFormUtil._createUpdateRequest<M, O>(original, changes, requiredPaths, options))
            .filter((request): request is UpdateRequest<M, O> => !!request);
    }

    /**
     * Adds `Validators.required` to every required field that received a common seed — the base
     * control, plus the end control of a date-range field — and, for each date-range field, a
     * {@link linkedRangeValidator} on both ends so a half-filled range blocks submit. Call it in
     * `ngAfterViewInit` before `populate()`, which touches every control: a declared `required`
     * on a mixed field would block submit on first render.
     *
     * @param formGroup - The group holding the value controls
     * @param seed - The result of {@link BulkUpdateFormUtil.seed}
     * @param fields - Every field the form can change across the selection
     */
    static addRequiredValidators<M>(
        formGroup: FormGroup,
        seed: IBulkUpdateSeed,
        fields: ReadonlyArray<IBulkUpdateField<M>>
    ): void {

        for (const field of fields) {
            const paths: Array<string> = field.kind === "dateRange" ? [field.path, field.linkedPath] : [field.path];

            if (field.kind === "dateRange") {
                formGroup.get(field.path)?.addValidators(BulkUpdateFormUtil.linkedRangeValidator(field.linkedPath));
                formGroup.get(field.linkedPath)?.addValidators(BulkUpdateFormUtil.linkedRangeValidator(field.path));
            }
            if (field.required && !seed.mixed.has(field.path) && seed.values[field.path] !== null) {
                for (const path of paths) {
                    formGroup.get(path)?.addValidators(Validators.required);
                }
            }
        }
    }

    /**
     * A validator for one end of a date-range field: `required` while the other end has a value, so
     * a half-filled range cannot be submitted. Pair it with {@link linkedRangeRevalidation$}.
     *
     * @param linkedPath - The form path of the other end of the range
     */
    static linkedRangeValidator(linkedPath: string): ValidatorFn {

        // assign function to a variable to avoid "Lambda not supported" errors in static functions
        const fn = (control: AbstractControl): ValidationErrors | null => {

            const other = control.parent?.get(linkedPath);
            const incomplete = other && BulkUpdateFormUtil._isEmpty(control.value) && !BulkUpdateFormUtil._isEmpty(other.value);
            return incomplete ? { required: true } : null;
        };
        return fn;
    }

    /**
     * Re-runs the validators of each date-range end when the other end changes, so the
     * {@link linkedRangeValidator} error appears and clears as the user fills the range. Subscribe
     * once in `ngOnInit` with `takeUntilDestroyed`.
     *
     * @param formGroup - The group holding the value controls
     * @param fields - Every field the form can change across the selection
     */
    static linkedRangeRevalidation$<M>(formGroup: FormGroup, fields: ReadonlyArray<IBulkUpdateField<M>>): Observable<void> {

        const streams: Array<Observable<void>> = [];

        for (const field of fields.filter((candidate) => candidate.kind === "dateRange")) {
            const ends = [formGroup.get(field.path), formGroup.get(field.linkedPath)];
            ends.forEach((control, index) => {
                const other = ends[1 - index];
                if (control && other) {
                    streams.push(control.valueChanges.pipe(
                        tap(() => {
                            const before = other.status;
                            other.updateValueAndValidity({ emitEvent: false });
                            // Only a real status flip is announced, which ends the ping-pong between the two ends
                            if (other.status !== before) {
                                other.updateValueAndValidity();
                            }
                        }),
                        map((): void => undefined)
                    ));
                }
            });
        }

        return merge(...streams);
    }

    /**
     * The value a checkbox field moves to when clicked or toggled by a hotkey. A mixed field cycles
     * `null` (leave) → `true` → `false` → `null`; a field with a common seed toggles between `true`
     * and `false`, and returning to the seed is a leave.
     *
     * @param current - The control's current value
     * @param mixed - Whether the selection disagrees on this field
     */
    static nextCheckboxValue(current: boolean | null | undefined, mixed: boolean): boolean | null {

        if (current === null || current === undefined) {
            return true;
        }
        if (current) {
            return false;
        }
        return mixed ? null : true;
    }

    /** Bulk field paths that are `@localized()` text fields on the model. */
    private static _localizedFieldPaths<M>(model: BulkUpdatableModel<M>, fields: ReadonlyArray<IBulkUpdateField<M>>): Array<string> {

        if (typeof (model as unknown as ILocalizableModel).setLocalizationValue !== "function") {
            return [];
        }
        const localized = LocalizationUtil.localizationTextFields(model as unknown as ILocalizableModel);
        return fields.map((field) => field.path as string).filter((path) => localized.includes(path));
    }

    /** A record's value for a localized field in one language, or `undefined`. */
    private static _localizedValue<M>(record: BulkUpdatableModel<M>, field: string, language: Language): unknown {

        const localization = (record as unknown as ILocalizableModel).getLocalization(language) as Record<string, unknown> | undefined;
        return localization?.[field];
    }

    /** Every non-leave change across the form's value paths. */
    private static _resolveChanges<M>(
        model: BulkUpdatableModel<M>,
        form: BulkUpdateFormState,
        fields: ReadonlyArray<IBulkUpdateField<M>>
    ): Array<IBulkUpdateChange> {

        const changes: Array<IBulkUpdateChange> = [];
        const localizedPaths = BulkUpdateFormUtil._localizedFieldPaths(model, fields);

        for (const field of fields) {
            const path = field.path as string;

            if (field.kind === "checkbox") {
                const checked = form.formGroup.get(path)?.value as unknown;
                if (typeof checked === "boolean" && form.isChangedAt(path)) {
                    changes.push({
                        path,
                        language: Language.English,
                        state: "set",
                        value: checked
                    });
                }
                continue;
            }
            if (field.kind === "dateRange") {
                changes.push(...BulkUpdateFormUtil._resolveRangeChanges(form, field));
                continue;
            }

            const languages = localizedPaths.includes(path) ? [Language.English, ...LOCALIZED_LANGUAGES] : [Language.English];

            for (const language of languages) {
                const clearable = language !== Language.English || !field.required;
                const value = BulkUpdateFormUtil._normalize(form.formGroup.get(BulkUpdateFormUtil.valuePath(path, language))?.value);
                const state = BulkUpdateFormUtil._resolveState(form, path, language, value, clearable);

                if (state !== "leave") {
                    changes.push({
                        path,
                        language,
                        state,
                        value
                    });
                }
            }
        }

        return changes;
    }

    /** Set / clear changes for a date-range field's two paths, per the class's date-range table. */
    private static _resolveRangeChanges<M>(form: BulkUpdateFormState, field: IBulkUpdateField<M>): Array<IBulkUpdateChange> {

        const startPath = field.path as string;
        const endPath = field.linkedPath as string;

        if (!form.formGroup.get(startPath) || !form.formGroup.get(endPath)) {
            return [];
        }

        const start = BulkUpdateFormUtil._toDate(form.formGroup.get(startPath).value);
        const end = BulkUpdateFormUtil._toDate(form.formGroup.get(endPath).value);
        const startSeed = form.snapshotValueAt(startPath);
        const endSeed = form.snapshotValueAt(endPath);
        const change = (path: string, state: "set" | "clear", value: unknown): IBulkUpdateChange => ({
            path,
            language: Language.English,
            state,
            value
        });

        if (!BulkUpdateFormUtil._isEmpty(start) && !BulkUpdateFormUtil._isEmpty(end)) {
            const differs = BulkUpdateFormUtil._timeOf(start) !== BulkUpdateFormUtil._timeOf(startSeed)
                || BulkUpdateFormUtil._timeOf(end) !== BulkUpdateFormUtil._timeOf(endSeed);
            return differs ? [change(startPath, "set", start), change(endPath, "set", end)] : [];
        }
        if (!BulkUpdateFormUtil._isEmpty(start) || !BulkUpdateFormUtil._isEmpty(end) || field.required) {
            return [];
        }

        const intent = form.formGroup.get(BulkUpdateFormUtil.clearIntentPath(startPath))?.value === true;
        const seeded = !BulkUpdateFormUtil._isEmpty(startSeed) || !BulkUpdateFormUtil._isEmpty(endSeed);
        return intent || seeded ? [change(startPath, "clear", undefined), change(endPath, "clear", undefined)] : [];
    }

    /** Leave / set / clear for one value path, per the class's state table. */
    private static _resolveState(
        form: BulkUpdateFormState, path: string, language: Language, value: unknown, clearable: boolean
    ): "leave" | "set" | "clear" {

        const valuePath = BulkUpdateFormUtil.valuePath(path, language);

        if (!form.formGroup.get(valuePath)) {
            return "leave";
        }
        if (!BulkUpdateFormUtil._isEmpty(value)) {
            return form.isChangedAt(valuePath) ? "set" : "leave";
        }
        if (!clearable) {
            return "leave";
        }
        if (form.formGroup.get(BulkUpdateFormUtil.clearIntentPath(path, language))?.value === true) {
            return "clear";
        }
        return BulkUpdateFormUtil._isEmpty(form.snapshotValueAt(valuePath)) ? "leave" : "clear";
    }

    /** The request for one record, or `undefined` when it has nothing to write. */
    private static _createUpdateRequest<M, O>(
        original: BulkUpdatableModel<M>,
        changes: ReadonlyArray<IBulkUpdateChange>,
        requiredPaths: ReadonlySet<string>,
        options: O | undefined
    ): UpdateRequest<M, O> | undefined {

        const merged = original.clone() as unknown as Record<string, unknown>;
        const originalValues = original as unknown as Record<string, unknown>;
        const clearedPaths: Array<string> = [];
        const assignedModels = new Map<string, unknown>();

        for (const change of changes) {
            if (change.language !== Language.English) {
                (merged as unknown as ILocalizableModel).setLocalizationValue(
                    change.path as never, change.state === "set" ? change.value as string : "", change.language
                );
            }
            else if (change.state === "set") {
                // An unset boolean is effectively false, so a record already at the target is not written
                if (typeof change.value === "boolean" && Boolean(originalValues[change.path]) === change.value) {
                    continue;
                }
                merged[change.path] = BulkUpdateFormUtil._copy(change.value);
                if (BulkUpdateFormUtil._isModelObject(change.value)) {
                    assignedModels.set(change.path, change.value);
                }
            }
            else if (!BulkUpdateFormUtil._isEmpty(originalValues[change.path])) {
                clearedPaths.push(change.path);
            }
        }

        const diff = original.getChanges(merged as unknown as IValidatedModel<M>);
        const fields: Record<string, unknown> = { ...diff.fields };

        // An assigned embedded model is sent whole, not as getChanges' sub-diff.
        for (const [path, value] of assignedModels) {
            if (path in fields) {
                fields[path] = (value as ISerializable).serialize?.() ?? value;
            }
        }

        const assignedPaths = Array.from(assignedModels.keys());
        const removeFields = [
            ...diff.removeFields.filter((field) => !assignedPaths.some((path) => field.startsWith(`${path}.`))),
            ...clearedPaths
        ].filter((field, index, all) => all.indexOf(field) === index && !requiredPaths.has(field));

        if (!Object.keys(fields).length && !removeFields.length) {
            return undefined;
        }

        return RequestUtil.createUpdateRequest<M, O>(
            original.id,
            ModelAction.ClientUpdate,
            ModelUtil.deserialize<M>(fields, original.modelName, { action: ModelAction.ClientUpdate }),
            original.esVersion,
            removeFields.length ? removeFields : undefined,
            options
        );
    }

    /** Strings sanitized; everything else as is. */
    private static _normalize(value: unknown): unknown {

        return typeof value === "string" ? StringUtil.sanitizeString(value) : value;
    }

    /** A non-array object, such as an embedded model like `MarketArea`. */
    private static _isModelObject(value: unknown): boolean {

        return typeof value === "object" && value !== null && !Array.isArray(value);
    }

    /** Writes one path's seed into `values`/`mixed`. */
    private static _seedPath(path: string, candidates: Array<unknown>, values: Record<string, unknown>, mixed: Set<string>): void {

        const [first] = candidates;

        if (candidates.every((candidate) => BulkUpdateFormUtil._sameValue(first, candidate))) {
            values[path] = BulkUpdateFormUtil._isEmpty(first) ? null : BulkUpdateFormUtil._copy(first);
        }
        else {
            values[path] = null;
            mixed.add(path);
        }
    }

    /** Writes a date-range field's two seeds: the common pair, or `null` and mixed for both ends. */
    private static _seedRange<M>(
        records: ReadonlyArray<BulkUpdatableModel<M>>,
        field: IBulkUpdateField<M>,
        values: Record<string, unknown>,
        mixed: Set<string>
    ): void {

        const starts = records.map((record) => (record as unknown as Record<string, unknown>)[field.path]);
        const ends = records.map((record) => (record as unknown as Record<string, unknown>)[field.linkedPath]);
        const agree = starts.every((start, index) => BulkUpdateFormUtil._sameValue(starts[0], start)
            && BulkUpdateFormUtil._sameValue(ends[0], ends[index]));

        for (const [path, candidates] of [[field.path, starts], [field.linkedPath, ends]] as Array<[string, Array<unknown>]>) {
            if (agree) {
                values[path] = BulkUpdateFormUtil._isEmpty(candidates[0]) ? null : BulkUpdateFormUtil._copy(candidates[0]);
            }
            else {
                values[path] = null;
                mixed.add(path);
            }
        }
    }

    /** Whether the field has a clear-intent control: not required, and not a checkbox. */
    private static _hasClearIntent<M>(field: IBulkUpdateField<M>): boolean {

        return !field.required && field.kind !== "checkbox";
    }

    /** A `Date` stays; a Luxon `DateTime` becomes its `Date`; anything else is as is. */
    private static _toDate(value: unknown): unknown {

        return DateTime.isDateTime(value) ? value.toJSDate() : value;
    }

    /** The instant of a `Date` or `DateTime`, or `undefined` for an empty or non-date value. */
    private static _timeOf(value: unknown): number | undefined {

        const date = BulkUpdateFormUtil._toDate(value);
        return date instanceof Date ? date.getTime() : undefined;
    }

    /** Equality for seeding: empties are equal, embedded models compare by `id`, other objects deeply. */
    private static _sameValue(a: unknown, b: unknown): boolean {

        if (BulkUpdateFormUtil._isEmpty(a) || BulkUpdateFormUtil._isEmpty(b)) {
            return BulkUpdateFormUtil._isEmpty(a) && BulkUpdateFormUtil._isEmpty(b);
        }
        if (Array.isArray(a) || Array.isArray(b)) {
            return JSON.stringify(a) === JSON.stringify(b);
        }
        if (a instanceof Date && b instanceof Date) {
            return a.getTime() === b.getTime();
        }
        if (typeof a === "object" && typeof b === "object") {
            const aId = (a as IIdentified).id;
            const bId = (b as IIdentified).id;
            if (aId !== undefined || bId !== undefined) {
                return aId === bId;
            }
            return !ObjectUtil.getChanges(a as object, b as object).hasChanges;
        }
        return a === b;
    }

    /** `null`, `undefined`, a blank string, or an empty array. */
    private static _isEmpty(value: unknown): boolean {

        return value === null
            || value === undefined
            || (typeof value === "string" && StringUtil.emptyString(value, true))
            || (Array.isArray(value) && value.length === 0);
    }

    /** A defensive copy — models via `clone()`, dates by instant, everything else as is. */
    private static _copy(value: unknown): unknown {

        if (value instanceof Date) {
            return new Date(value.getTime());
        }
        const cloneable = value as ICloneable | null;
        return typeof cloneable?.clone === "function" ? cloneable.clone() : value;
    }
}
